import { Megaphone, X } from "lucide-react";
import { useEffect, useRef } from "react";

import type { GtmBrainDoc } from "../api/GtmApi";
import {
  influencerSections,
  listInfluencers,
  type InfluencerFieldId,
} from "./influencers";

type Props = {
  docs: GtmBrainDoc[];
  influencerKey: string;
  onDocsChange: (docs: GtmBrainDoc[]) => void;
  onDiscard: () => void;
  onBackToInfluencers: () => void;
  onBackToOverview: () => void;
};

export default function BrainInfluencerDocView({
  docs,
  influencerKey,
  onDocsChange,
  onDiscard,
  onBackToInfluencers,
  onBackToOverview,
}: Props) {
  const influencers = listInfluencers(docs);
  const item = influencers.find((i) => i.key === influencerKey) || influencers[0];
  const sections = item ? influencerSections(item.doc) : [];
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [influencerKey]);

  if (!item) {
    return (
      <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 items-center justify-center border-r border-slate-200 bg-white text-sm text-slate-500">
        Influencer not found.
      </div>
    );
  }

  const updateField = (field: InfluencerFieldId, value: string) => {
    const next = docs.map((d, i) => (i === item.index ? { ...d, [field]: value } : d));
    onDocsChange(next);
  };

  const scrollToSection = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <div className="min-w-0 truncate">
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToOverview}
          >
            Brain
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToInfluencers}
          >
            Influencers and experts
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <span className="font-medium text-slate-800">{item.label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={onBackToInfluencers}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
          <Megaphone className="h-5 w-5 text-amber-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">{item.label}</h2>
        <p className="mt-1 text-sm text-slate-500">
          Why this market reference matters and how you position around it.
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          <section data-section-id="name" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">
              Name <span className="font-normal text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={item.doc.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="Name this reference."
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="domain" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Domain</label>
            <input
              type="text"
              value={item.doc.domain || ""}
              onChange={(e) => updateField("domain", e.target.value)}
              placeholder="example.com"
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="linkedinUrl" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">LinkedIn URL</label>
            <input
              type="text"
              value={item.doc.linkedinUrl || ""}
              onChange={(e) => updateField("linkedinUrl", e.target.value)}
              placeholder="linkedin.com/..."
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="description" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Summary</label>
            <textarea
              value={item.doc.description || ""}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="What they do, in a sentence or two."
              rows={3}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="notes" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Positioning notes</label>
            <textarea
              value={item.doc.notes || ""}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="How you win against them, or why they matter."
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>
        </div>
      </div>
    </div>
  );
}
